import type { MonthViewModel } from "../domain/views.ts";
import type { Dataset, MonthId } from "../domain/types.ts";
import { chargesForPurchaseInMonth, sliceAmountForMonth } from "../domain/charges.ts";
import { toBase } from "../domain/fx.ts";
import { roundMoney } from "../domain/money.ts";

export type MonthPieMode = "spending" | "allocation";
export interface MonthPieSlice {
  id: string;
  label: string;
  amount: number;
}

/** Read-only chart selection; shares reuse the same charge boundaries as the fold. */
export function spendingPieData(dataset: Dataset, monthId: MonthId, excluded: ReadonlySet<string>) {
  const groups = new Map<string, { post: MonthPieSlice; purchases: MonthPieSlice[] }>();
  const { baseCurrency, digits } = dataset.settings;
  const labels = new Map(dataset.posts.map(post => [post.id, post.name]));
  for (const purchase of dataset.purchases) {
    const slice = sliceAmountForMonth(purchase, monthId);
    if (!slice) continue;
    const charges = purchase.splits.length === 0
      ? [{ id: "unassigned", label: "Unassigned", amount: toBase(slice, baseCurrency, dataset.fxRates, digits) }]
      : chargesForPurchaseInMonth(purchase, monthId, baseCurrency, dataset.fxRates, digits)
        .map(charge => ({ id: `post:${charge.postId}`, label: labels.get(charge.postId) ?? charge.postId, amount: charge.amount }));
    for (const charge of charges) {
      if (charge.amount === 0) continue;
      let group = groups.get(charge.id);
      if (!group) {
        group = { post: { ...charge, amount: 0 }, purchases: [] };
        groups.set(charge.id, group);
      }
      group.post.amount = roundMoney(group.post.amount + charge.amount, digits);
      const existing = group.purchases.find(item => item.id === purchase.id);
      if (existing) existing.amount = roundMoney(existing.amount + charge.amount, digits);
      else group.purchases.push({ id: purchase.id, label: purchase.description || "Untitled purchase", amount: charge.amount });
    }
  }
  const posts = [...groups.values()].map(group => group.post)
    .sort((a, b) => b.amount - a.amount || a.label.localeCompare(b.label));
  const selected = posts.filter(post => !excluded.has(post.id));
  const focus = selected.length === 1 ? groups.get(selected[0]!.id)! : null;
  const items = focus ? focus.purchases : selected;
  return {
    posts,
    selectedCount: selected.length,
    focusLabel: focus?.post.label ?? null,
    chart: {
      positive: items.filter(item => item.amount > 0).sort((a, b) => b.amount - a.amount || a.label.localeCompare(b.label)),
      adjustments: items.filter(item => item.amount < 0).sort((a, b) => a.amount - b.amount),
      positiveTotal: roundMoney(items.reduce((sum, item) => sum + Math.max(0, item.amount), 0), digits),
      netTotal: roundMoney(selected.reduce((sum, item) => sum + item.amount, 0), digits),
    },
  };
}

/** Chart data comes from the same folded figures as the month table. */
export function monthPieData(view: MonthViewModel, mode: MonthPieMode) {
  const items: MonthPieSlice[] = view.rows.map(({ post, figures }) => ({
    id: post.id,
    label: post.name,
    amount: mode === "spending" ? figures.charges : figures.allocation,
  }));
  if (mode === "spending" && view.looseCharges !== 0) {
    items.push({ id: "unassigned", label: "Unassigned", amount: view.looseCharges });
  }
  const byAmount = (a: MonthPieSlice, b: MonthPieSlice) => b.amount - a.amount || a.label.localeCompare(b.label);
  const positive = items.filter((item) => item.amount > 0).sort(byAmount);
  const adjustments = items.filter((item) => item.amount < 0).sort((a, b) => a.amount - b.amount);
  return {
    positive,
    adjustments,
    positiveTotal: positive.reduce((sum, item) => sum + item.amount, 0),
    netTotal: mode === "spending" ? view.totalCharges : view.totalAllocation,
  };
}
